import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, Zap, Shield, ArrowRight, Mail, Lock, User, Eye, EyeOff, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ENLogo } from '@/components/ENLogo';

export default function WebsiteBuilderAuth() {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect to website builder
  if (user) {
    return <Navigate to="/website-builder" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) { toast.error('Please enter your name'); setLoading(false); return; }
        await register(email, password, name, 'consumer');
        toast.success('Account created! Starting your website setup...');
      } else {
        await login(email, password);
        toast.success('Welcome back!');
      }
      navigate('/website-builder', { replace: true });
    } catch (error) {
      const msg = error.message || 'Something went wrong';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        toast.error('Unable to connect. Please check your internet and try again.');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Globe, title: 'Custom Domain', desc: 'Connect your own domain or use a free subdomain' },
    { icon: Zap, title: 'Drag & Drop Editor', desc: 'Build beautiful pages without coding' },
    { icon: Shield, title: '14-Day Free Trial', desc: 'Try everything free, no credit card required' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white flex" data-testid="website-builder-auth">
      {/* Background grid */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      {/* Left - Hero */}
      <div className="hidden lg:flex flex-1 flex-col justify-center px-16 relative">
        <div className="absolute top-8 left-8">
          <ENLogo size="md" linkTo="/" />
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h1 className="font-syne text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Create Your<br /><span className="text-holographic">Website</span> in Minutes
          </h1>
          <p className="text-[#9CA3AF] text-lg max-w-md mb-10">
            No coding required. Build a professional website with our drag-and-drop builder. Start your 14-day free trial today.
          </p>

          <div className="space-y-5">
            {features.map((f, i) => (
              <motion.div key={i} className="flex items-start gap-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.1 }}>
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#8B5CF6]/20 to-[#3B82F6]/20 flex-shrink-0">
                  <f.icon size={20} className="text-[#A78BFA]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{f.title}</p>
                  <p className="text-[#9CA3AF] text-sm">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div className="w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-block"><ENLogo size="md" linkTo="/" /></div>
            <h2 className="font-syne text-2xl font-bold mt-4">Create Your <span className="text-holographic">Website</span></h2>
          </div>

          <div className="bg-[#1E1E3A]/80 backdrop-blur-2xl border border-white/10 rounded-2xl p-8 shadow-[0_8px_32px_0_rgba(0,0,0,0.6)]">
            {/* Toggle */}
            <div className="flex gap-1 bg-[#0E0E23]/60 rounded-xl p-1 mb-8">
              <button onClick={() => setMode('signup')} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === 'signup' ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-lg' : 'text-[#9CA3AF]'}`} data-testid="wb-auth-signup-tab">
                Sign Up
              </button>
              <button onClick={() => setMode('login')} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${mode === 'login' ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-lg' : 'text-[#9CA3AF]'}`} data-testid="wb-auth-login-tab">
                Log In
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'signup' && (
                <div>
                  <Label className="text-[#9CA3AF] text-xs mb-1 block">Full Name</Label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-white/5 border-white/10 text-white rounded-xl h-11 pl-10 placeholder:text-white/20" required data-testid="wb-auth-name" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-[#9CA3AF] text-xs mb-1 block">Email</Label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="bg-white/5 border-white/10 text-white rounded-xl h-11 pl-10 placeholder:text-white/20" required data-testid="wb-auth-email" />
                </div>
              </div>

              <div>
                <Label className="text-[#9CA3AF] text-xs mb-1 block">Password</Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                  <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === 'signup' ? 'Create a password' : 'Your password'} className="bg-white/5 border-white/10 text-white rounded-xl h-11 pl-10 pr-10 placeholder:text-white/20" required data-testid="wb-auth-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all flex items-center justify-center gap-2" data-testid="wb-auth-submit">
                {loading ? 'Please wait...' : mode === 'signup' ? 'Start Building Your Website' : 'Log In & Continue Building'}
                <ArrowRight size={16} />
              </button>
            </form>

            {mode === 'signup' && (
              <div className="mt-6 space-y-2">
                {['14-day free trial, no credit card', 'Free subdomain included', 'Cancel anytime'].map((text, i) => (
                  <div key={i} className="flex items-center gap-2 text-[#9CA3AF] text-xs"><Check size={12} className="text-[#86EFAC]" />{text}</div>
                ))}
              </div>
            )}

            <p className="text-center text-[#64748B] text-xs mt-6">
              {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')} className="text-[#A78BFA] hover:text-white transition-colors">
                {mode === 'signup' ? 'Log in' : 'Sign up'}
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
